/**
 * 販売者用レイアウト
 *
 * @file SellerLayout.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 販売者向け管理画面の共通レイアウト。
 * ヘッダー、ユーザーメニュー、サイドバーナビゲーションを提供。
 */

import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import MockBanner from '@shared/components/MockBanner';
import { useAuth } from '@shared/contexts/useAuth';
import { useLanguage, I18nLoadingScreen } from '@shared/contexts/LanguageContext';
import { ensureInitialized } from '@shared/i18n';

interface SellerLayoutProps {
  children?: React.ReactNode;
}

const SellerLayout: React.FC<SellerLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isInitialized, setIsInitialized] = useState(false);

  // i18nextの初期化
  useEffect(() => {
    ensureInitialized().then(() => {
      setIsInitialized(true);
    });
  }, []);

  // メニュー外クリック時の自動クローズ（ユーザーメニューとモバイルサイドバー）
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // ユーザーメニューのクローズ処理
      const userMenu = document.getElementById('userMenu');
      const sellerUser = target.closest('.seller-user');
      
      if (showUserMenu && userMenu && !sellerUser) {
        setShowUserMenu(false);
      }
      
      // モバイルサイドバーのクローズ処理
      if (isMobile) {
        const sidebar = target.closest('.seller-sidebar');
        const toggleButton = target.closest('.sidebar-toggle');
        
        if (!sidebar && !toggleButton) {
          handleSidebarClose();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showUserMenu, isMobile]);

  // 画面サイズ変更時のレスポンシブ対応
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        handleSidebarClose();
      }
      else {
        document.body.classList.remove('sidebar-collapsed');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ページ遷移時にスクロールをトップ、またはハッシュ指定要素に戻す
  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'instant' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, location.hash]);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  }

  const handleUserMenuClose = () => {
    setShowUserMenu(false);
  };

  const handleLogout = async () => {
    try {
      handleUserMenuClose();
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // エラー発生時もログイン画面へ遷移
    navigate('/login');
  };

  // サイドバー切り替え機能
  const handleSidebarToggle = () => {
    document.body.classList.toggle('sidebar-collapsed');
  };

  const handleSidebarClose = () => {
    document.body.classList.add('sidebar-collapsed');
  };

  // i18next初期化完了まで待機
  if (!isInitialized) {
    return <I18nLoadingScreen />;
  }

  return (
    <div className="seller-layout">
      {/* モックバナー */}
      <MockBanner />

      {/* 共通ヘッダー */}
      <header className="seller-header">
        <div className="seller-header-container">

          {/* ハンバーガーメニュー */}
          <div className="sidebar-toggle" onClick={handleSidebarToggle}>
            <i className="material-icons">menu</i>
          </div>

          {/* ヘッダーロゴ */}
          <div className="seller-logo">
            <Link to="/seller" className="seller-logo-text">
              <div className="seller-site-name">{t('layout.seller.header.logo')}</div>
              <div className="seller-system-name">{t('layout.seller.header.system_name')}</div>
            </Link>
          </div>

          {/* ヘッダーナビゲーション */}
          <div className="nav-links">
            <Link to="/seller/help" className="help-link">
              <i className="material-icons">help_outline</i>
              <span>{t('layout.seller.header.help')}</span>
            </Link>
            <div className="seller-user" onClick={handleUserMenuToggle}>
              <div className="seller-user-avatar">T</div>
              <div className="seller-user-name">{user?.firstName}</div>
              <i className="material-icons">arrow_drop_down</i>
              
              {/* ユーザーメニュー */}
              <div className={`user-menu ${showUserMenu ? 'show' : ''}`} id="userMenu" style={{ display: showUserMenu ? 'block' : 'none' }}>
                {/* ユーザー情報ヘッダー */}
                <div className="user-menu-header">
                  <div className="user-menu-avatar">T</div>
                  <div className="user-menu-info">
                    <div className="user-menu-name">{user?.firstName}</div>
                    <div className="user-menu-email">{user?.email}</div>
                  </div>
                </div>

                <hr className="user-menu-divider" />

                {/* メニュー項目 */}
                <Link to="/seller/info" className={`user-menu-item ${location.pathname === '/seller/info' ? 'active' : ''}`}>
                  <i className="material-icons">store</i>
                  <span>{t('layout.seller.user_menu.store_settings')}</span>
                </Link>
                <Link to="/seller/notifications" className={`user-menu-item ${location.pathname === '/seller/notifications' ? 'active' : ''}`}>
                  <i className="material-icons">notifications</i>
                  <span>{t('layout.seller.user_menu.notification_settings')}</span>
                </Link>
                <Link to="/seller/account" className={`user-menu-item ${location.pathname === '/seller/account' ? 'active' : ''}`}>
                  <i className="material-icons">account_circle</i>
                  <span>{t('layout.seller.user_menu.account')}</span>
                </Link>

                <hr className="user-menu-divider" />

                <div className="user-menu-item logout" onClick={handleLogout}>
                  <i className="material-icons">logout</i>
                  <span>{t('layout.seller.user_menu.logout')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 共通サイドバー */}
      <aside className="seller-sidebar">
        <div className="nav-group">
          <div className="nav-group-title">{t('layout.seller.sidebar.main_menu.title')}</div>
          <Link to="/seller" className={`nav-item ${location.pathname === '/seller' ? 'active' : ''}`}>
            <i className="material-icons">dashboard</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.dashboard')}</span>
          </Link>
          <Link to="/seller/products/new" className={`nav-item ${location.pathname === '/seller/products/new' ? 'active' : ''}`}>
            <i className="material-icons">shopping_bag</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.product_register')}</span>
          </Link>
          <Link to="/seller/inventory/list" className={`nav-item ${location.pathname.startsWith('/seller/inventory') ? 'active' : ''}`}>
            <i className="material-icons">inventory</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.inventory')}</span>
          </Link>
          <Link to="/seller/orders" className={`nav-item ${location.pathname === '/seller/orders' ? 'active' : ''}`}>
            <i className="material-icons">add_shopping_cart</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.orders')}</span>
          </Link>
          <Link to="/seller/payments" className={`nav-item ${location.pathname === '/seller/payments' ? 'active' : ''}`}>
            <i className="material-icons">account_balance_wallet</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.payments')}</span>
          </Link>
          <Link to="/seller/coupons" className={`nav-item ${location.pathname === '/seller/coupons' ? 'active' : ''}`}>
            <i className="material-icons">local_offer</i>
            <span className="nav-label">{t('layout.seller.sidebar.main_menu.coupons')}</span>
          </Link>
        </div>

        <div className="nav-group">
          <div className="nav-group-title">{t('layout.seller.sidebar.analytics.title')}</div>
          <Link to="/seller/analytics/sales" className={`nav-item ${location.pathname === '/seller/analytics/sales' ? 'active' : ''}`}>
            <i className="material-icons">bar_chart</i>
            <span className="nav-label">{t('layout.seller.sidebar.analytics.sales')}</span>
          </Link>
          <Link to="/seller/analytics/products" className={`nav-item ${location.pathname === '/seller/analytics/products' ? 'active' : ''}`}>
            <i className="material-icons">trending_up</i>
            <span className="nav-label">{t('layout.seller.sidebar.analytics.products')}</span>
          </Link>
          <Link to="/seller/analytics/buyers" className={`nav-item ${location.pathname === '/seller/analytics/buyers' ? 'active' : ''}`}>
            <i className="material-icons">people</i>
            <span className="nav-label">{t('layout.seller.sidebar.analytics.buyers')}</span>
          </Link>
        </div>

        <div className="nav-group">
          <div className="nav-group-title">{t('layout.seller.sidebar.support.title')}</div>
          <Link to="/seller/chat-support" className={`nav-item ${location.pathname === '/seller/chat-support' ? 'active' : ''}`}>
            <i className="material-icons">chat</i>
            <span className="nav-label">{t('layout.seller.sidebar.support.chat')}</span>
          </Link>
          <Link to="/seller/help" className={`nav-item ${location.pathname === '/seller/help' ? 'active' : ''}`}>
            <i className="material-icons">help</i>
            <span className="nav-label">{t('layout.seller.sidebar.support.help_faq')}</span>
          </Link>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <main className="main-content">
        {children ? children : <Outlet />}
      </main>

      {/* 共通フッター */}
      <footer className="seller-footer">
        <div className="seller-footer-container">
          <div>{t('common.brand.copyright')}</div>
          <div className="footer-links">
            <Link to="#">{t('layout.seller.footer.links.help')}</Link>
            <Link to="#">{t('layout.seller.footer.links.privacy')}</Link>
            <Link to="#">{t('layout.seller.footer.links.terms')}</Link>
            <Link to="/seller/help#contact-section">{t('layout.seller.footer.links.contact')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SellerLayout;
